import { Check, DeleteOutlined, LockOutlined, MailOutlineSharp, MoreVert, OpenInNew, PhoneDisabled, Remove } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import {
	type Dispatch,
	type FC,
	type MouseEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SetPasswordDialog } from "../../../../components/SetPasswordDialog/SetPasswordDialog";
import type { MetaChipTone } from "../../../../components/UIToolkit/Badges/MetaChip";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { SystemChips } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { type GrantBarSegment, SystemGrantBars } from "../../../../components/UIToolkit/DataViz/SystemGrantBars/SystemGrantBars";
import { SystemDataGrid } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { SearchableColumn, SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid/types";
import { extractSearchValue } from "../../../../components/UIToolkit/Tables/SystemDataGrid/utils";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useErrorSnackbar } from "../../../../hooks/useErrorSnackbar";
import type { TranslationKeys } from "../../../../i18n/types";
import { backendService } from "../../../../services/backend.service";
import { teamAdminsGridKey } from "../../../../services/LocalStorageService";
import { FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import { AdminAccessPanel } from "./AdminAccessPanel";
import {
	ADMIN_NOTIFICATION_ITEMS,
	ADMIN_PERMISSION_CATALOG,
	ADMIN_PERMISSION_ITEMS,
	type AdminPermissionMeta,
	isGranted,
	needsPhoneNumber,
} from "./adminPermissionCatalog";
import { ADMIN_ROLES, customizedPermissions, type PermissionSource, permissionSource } from "./adminRoles";
import { useAdminRoleDefaults, useSendPasswordResetNotification } from "./adminUsersHooks";
import type { AdminPermission, AdminRole, AdminUser } from "./types";

/**
 * A tone per role, so a role is legible while scanning the grid. More roles than tones, so two
 * reuse one — the label is what tells them apart. Theme tokens, never hardcoded colors.
 */
const ROLE_TONES: Record<AdminRole, MetaChipTone> = {
	Scheduler: "primary",
	Admin: "info",
	Supervisor: "warning",
	HR: "success",
	Viewer: "neutral",
	NursingLeadership: "info",
	Network: "neutral",
};

/** Picker sections: access, email digests and identity are three different things. */
const PICKER_GROUP = {
	PLAIN: "plain",
	PERMISSIONS: "permissions",
	NOTIFICATIONS: "notifications",
} as const;

type AdminUserRow = AdminUser & {
	_gridId: string;
	_isNewRow: boolean;
	_rowIndex: number;
	_isEdited: boolean;
	/** Permissions that disagree with what this row's roles grant. Derived, never stored. */
	_customized: AdminPermission[];
};

// Shared with the footer count so the two can't drift.
const SEARCHABLE_COLUMNS: SearchableColumn<AdminUserRow>[] = ["firstName", "username", "phoneNumber"];

// Excludes `rowDisabled` — that's a transient UI flag, not a persisted field.
const EDIT_TRACKED_FIELDS: readonly (keyof AdminUser)[] = [
	"adminRoles",
	"firstName",
	"phoneNumber",
	"username",
	"requiresMFA",
	"isReadonly",
	"hideWage",
	"disableEditMasterSchedule",
	"callOffAdmin",
	"support",
	"modifyUser",
	"receiveDailyAdminNotifications",
	"receiveActionRequiredAdminNotifications",
	"sendDailyActualShiftsCheck",
	"sendDailyPickupSummary",
	"receivePerformanceReport",
	"receiveDailyExcelReport",
	"receiveSmsResponseNotifications",
	"receiveNurseInactivityNotifications",
] as const;

/**
 * Makes a field comparable, for spotting an edited row.
 *
 * Null and empty string count as equal, so an absent BE field is not a false edit. Arrays become a
 * sorted string: two arrays are never equal by reference, so every row would look edited forever.
 */
const normalizeForCompare = (v: unknown): unknown => {
	if (v === undefined || v === null) return "";
	if (Array.isArray(v)) return [...v].map(String).sort().join("|");
	return v;
};

const isExistingRowEdited = (current: AdminUser, original: AdminUser | undefined): boolean => {
	if (!original) return false;
	return EDIT_TRACKED_FIELDS.some((k) => normalizeForCompare(current[k]) !== normalizeForCompare(original[k]));
};

const MoreActionsMenu: FC<{
	userId: string | undefined;
	userName?: string;
	userEmail?: string;
	allowNotifyEmail: boolean;
	allowNotifySMS: boolean;
	onRemove?: () => void;
	onOpenPanel?: () => void;
	disabled?: boolean;
}> = ({ userId, userName, userEmail, allowNotifyEmail, allowNotifySMS, onRemove, onOpenPanel, disabled }) => {
	const showErrorSnackbar = useErrorSnackbar();
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [setPasswordOpen, setSetPasswordOpen] = useState(false);
	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);
	const open = !!anchorEl;

	const { mutateAsync: sendPasswordReset, isPending: isSendingEmail } = useSendPasswordResetNotification({ userId });

	/** Sends the link down every channel the person has. The channel is not a choice to make here. */
	const sendUserWelcomeNotification = useCallback(async () => {
		const channels: ("email" | "sms")[] = [...(allowNotifyEmail ? (["email"] as const) : []), ...(allowNotifySMS ? (["sms"] as const) : [])];
		track("admin_password_reset_start", { user_id: userId, channels: channels.join(",") });
		try {
			const results = await Promise.allSettled(channels.map((channel) => sendPasswordReset(channel)));
			const failed = results.filter((result) => result.status === "rejected");
			// All channels failing is an error; one of two still got the link there, so warn instead.
			if (failed.length === channels.length) throw new Error(t("admin.menu.resetPassword.failed"));
			showSnackbar(failed.length > 0 ? t("admin.menu.resetPassword.partial") : t("admin.menu.resetPassword.sent"), failed.length > 0 ? "warning" : "success");
			track("admin_password_reset_success", { user_id: userId, channels: channels.join(","), failed_channels: failed.length });
			handleClose();
		} catch (e) {
			if (e instanceof Error) {
				trackError("admin_password_reset_error", { user_id: userId, channels: channels.join(","), error_message: e.message });
				showErrorSnackbar(e.toString(), {}, e);
			}
		}
	}, [sendPasswordReset, allowNotifyEmail, allowNotifySMS, showErrorSnackbar, showSnackbar, handleClose, t, track, trackError, userId]);

	return (
		<>
			<IconButton
				disabled={disabled}
				onClick={handleClick}
				id="more-actions-menu-button"
				aria-controls={open ? "basic-menu" : undefined}
				aria-haspopup="true"
				aria-expanded={open ? "true" : undefined}
			>
				<MoreVert sx={{ fontSize: "20px" }} />
			</IconButton>
			<Menu
				id="more-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{
					list: { "aria-labelledby": "basic-button" },
				}}
			>
				{(userName || userEmail) && (
					<Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${NEUTRAL[200]}`, mb: 0.5 }}>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}>{userName || t("admin.panel.unnamed")}</Typography>
						{userEmail && <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{userEmail}</Typography>}
					</Box>
				)}
				{onOpenPanel && (
					<MenuItem
						onClick={() => {
							onOpenPanel();
							handleClose();
						}}
					>
						<OpenInNew sx={{ mx: 0.5, fontSize: 18, color: INK.MUTED }} />
						<Box component="span">{t("admin.menu.openPanel")}</Box>
					</MenuItem>
				)}
				{userInfo?.support && (
					<MenuItem
						onClick={() => {
							setSetPasswordOpen(true);
							handleClose();
						}}
						disabled={!userId}
					>
						<LockOutlined sx={{ mx: 0.5, fontSize: 18, color: INK.MUTED }} />
						<Box component="span">{t("admin.menu.setPassword")}</Box>
					</MenuItem>
				)}
				<MenuItem
					onClick={() => {
						sendUserWelcomeNotification();
					}}
					disabled={isSendingEmail || (!allowNotifyEmail && !allowNotifySMS)}
				>
					<MailOutlineSharp sx={{ mx: 0.5, fontSize: 18, color: INK.MUTED }} />
					<Box component="span">{t("admin.menu.resetPassword")}</Box>
				</MenuItem>
				{onRemove && (
					<MenuItem
						onClick={() => {
							onRemove();
							handleClose();
						}}
						disabled={disabled}
						sx={{ color: "error.main" }}
					>
						<DeleteOutlined sx={{ mx: 0.5, fontSize: 18, color: "error.main" }} />
						<Box component="span">{t("admin.menu.remove")}</Box>
					</MenuItem>
				)}
			</Menu>
			{userId && <SetPasswordDialog open={setPasswordOpen} onClose={() => setSetPasswordOpen(false)} userId={userId} userName={userName} />}
		</>
	);
};

/**
 * Email above, mobile below — the email is the sign-in, so it identifies the account. A missing
 * mobile says so, in amber when something on the row is sent by text.
 */
const ContactDetailsCell: FC<{ row: AdminUserRow }> = ({ row }) => {
	const { t } = useTranslation();
	const phoneIsNeeded = ADMIN_PERMISSION_CATALOG.some((meta) => needsPhoneNumber(meta.permission) && isGranted(row, meta));
	const phone = row.phoneNumber?.trim();

	return (
		<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.125, width: "100%", minWidth: 0 }}>
			<Typography
				sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
				aria-label={t("admin.contact.emailAriaLabel", { name: row.firstName?.trim() || t("admin.contact.thisAdmin") })}
			>
				{row.username || t("admin.contact.emailPlaceholder")}
			</Typography>
			{phone ? (
				<Typography
					sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}
					aria-label={t("admin.contact.phoneAriaLabel", { name: row.firstName?.trim() || t("admin.contact.thisAdmin") })}
				>
					{phone}
				</Typography>
			) : (
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					{phoneIsNeeded && <PhoneDisabled sx={{ fontSize: 12, color: "warning.main" }} />}
					<Typography
						sx={{ fontSize: FONT_SIZE.CAPTION, fontStyle: "italic", color: phoneIsNeeded ? "warning.main" : INK.MUTED }}
						title={phoneIsNeeded ? t("admin.contact.phoneRequired") : undefined}
					>
						{t("admin.contact.noMobile")}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

/** Last sign-in as "12 min ago" — which of these accounts is actually in use. */
const LastSignInCell: FC<{ isoTimestamp: string | null | undefined }> = ({ isoTimestamp }) => {
	const { t } = useTranslation();
	if (!isoTimestamp) {
		return <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.DISABLED, fontStyle: "italic" }}>{t("admin.lastSignIn.never")}</Typography>;
	}
	const signedInAt = DateTime.fromISO(isoTimestamp);
	if (!signedInAt.isValid) {
		return <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.DISABLED }}>{"--"}</Typography>;
	}
	return (
		<Typography
			sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, fontVariantNumeric: "tabular-nums" }}
			title={signedInAt.toFormat("ccc, MMM d, yyyy HH:mm")}
		>
			{signedInAt.toRelative()}
		</Typography>
	);
};

/**
 * One permission column from the Columns menu — a read-only marker, not a control.
 *
 * The expanded view is for comparing one permission down the list; editing happens in the panel.
 * State and its reason read as color, and the header stays on one line to fit a narrow column.
 */
const buildGrantColumn = (
	meta: AdminPermissionMeta,
	label: string,
	headerTooltip: ReactNode,
	group: string,
	customizedOf: (row: AdminUserRow) => AdminPermission[],
): SystemGridColDef<AdminUserRow> => ({
	field: meta.field,
	headerName: label,
	headerTooltip,
	width: 128,
	sortable: false,
	filterable: false,
	disableColumnMenu: true,
	align: "center",
	headerAlign: "center",
	// Not listed individually: the group's row toggles the whole block, so no switch appears twice.
	excludeFromColumnPicker: true,
	headerGroup: group,
	renderCell: ({ row }) => {
		const granted = isGranted(row, meta);
		const isCustom = customizedOf(row).includes(meta.permission);
		return (
			<Box
				data-testid={`admin-flag-${meta.permission}-${row._gridId}`}
				data-granted={granted}
				sx={{ display: "grid", placeItems: "center", width: 18, height: 18, mx: "auto" }}
			>
				{granted ? (
					<Check sx={{ fontSize: 17, color: isCustom ? "info.main" : "success.main" }} />
				) : (
					<Remove sx={{ fontSize: 15, color: isCustom ? "info.main" : NEUTRAL[400] }} />
				)}
			</Box>
		);
	},
});

/** The grid's state text for a switch, from the same source as the panel's line. */
const sourceStateKey = (source: PermissionSource): TranslationKeys => {
	switch (source.kind) {
		case "custom":
			return source.addedForPerson ? "admin.permission.state.addedForPerson" : "admin.permission.state.removedForPerson";
		case "fromRoles":
			return "admin.permission.state.granted";
		case "onWithoutRole":
			return "admin.panel.source.onWithoutRole";
		case "notGranted":
			return "admin.permission.state.notGranted";
	}
};

/** What the marker colors mean. */
const BarLegend: FC = () => {
	const { t } = useTranslation();
	const swatch = (color: string, outlined?: boolean) => (
		<Box sx={{ width: 8, height: 14, borderRadius: 0.5, backgroundColor: color, border: outlined ? `1px solid ${NEUTRAL[400]}` : "none", flexShrink: 0 }} />
	);
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 0.5 }} data-testid="admin-bar-legend">
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
				{swatch("success.main")}
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("admin.legend.granted")}</Typography>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
				{swatch("info.main")}
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("admin.legend.custom")}</Typography>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
				{swatch(NEUTRAL[200], true)}
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("admin.legend.notGranted")}</Typography>
			</Box>
		</Box>
	);
};

export const AdminsAndPermissions: FC<{
	existingUsers: AdminUser[];
	setExistingUsers: Dispatch<SetStateAction<AdminUser[]>>;
	/** Last-saved baseline for `existingUsers`; diffed per-row to mark unsaved edits. */
	originalExistingUsers: AdminUser[];
	newUsers: AdminUser[];
	setNewUsers: Dispatch<SetStateAction<AdminUser[]>>;
	/** Stable React keys for `newUsers`, owned by the parent. Length must mirror `newUsers`. */
	newUserKeys: string[];
	isUpdatingUsers: boolean;
	onRemoveUser?: (userId: string) => void;
	currentUserId?: string;
	actionNodes?: ReactNode;
	/** Saves all state; resolves false when it did not commit, so the panel can stay open. */
	onSave?: () => Promise<boolean | undefined>;
	/** Bumped when the parent appends a draft row; the panel opens on it, so create == edit. */
	newRowRequestCount?: number;
	/** Drops a draft row by index. The parent owns it so the row and its key stay together. */
	onDiscardNewUser?: (index: number) => void;
}> = ({
	existingUsers,
	setExistingUsers,
	originalExistingUsers,
	newUsers,
	setNewUsers,
	newUserKeys,
	isUpdatingUsers,
	onRemoveUser,
	currentUserId,
	actionNodes,
	onSave,
	newRowRequestCount = 0,
	onDiscardNewUser,
}) => {
	const { t } = useTranslation();
	const { data: roleDefaults, isError: roleDefaultsFailed } = useAdminRoleDefaults();
	const { showSnackbar } = useSnackbar();
	const showErrorSnackbar = useErrorSnackbar();
	const { userInfo } = useAuth();
	const { track, trackError } = useAnalytics();

	// Which row the panel shows, by `_gridId` so it survives the rows memo rebuilding. Null = closed.
	const [openPanelGridId, setOpenPanelGridId] = useState<string | null>(null);
	const [panelSetPasswordOpen, setPanelSetPasswordOpen] = useState(false);
	const [isResettingPassword, setIsResettingPassword] = useState(false);

	/**
	 * Sends the reset link for whichever row is open, down every channel it has.
	 *
	 * Not `useSendPasswordResetNotification`: that hook binds one `userId` at mount.
	 */
	const resetPasswordForOpenRow = useCallback(
		async (userId: string, hasEmail: boolean, hasPhone: boolean) => {
			// One at a time: each new token kills the last, so a double-click sends a dead link.
			if (isResettingPassword) return;
			const channels: ("email" | "sms")[] = [...(hasEmail ? (["email"] as const) : []), ...(hasPhone ? (["sms"] as const) : [])];
			setIsResettingPassword(true);
			track("admin_password_reset_start", { user_id: userId, channels: channels.join(","), source: "panel" });
			try {
				const results = await Promise.allSettled(channels.map((channel) => backendService.sendWelcomeEmail({ userId, channel })));
				const failed = results.filter((result) => result.status === "rejected" || result.value.status >= 400);
				if (failed.length === channels.length) {
					throw new Error(t("admin.menu.resetPassword.failed"));
				}
				showSnackbar(failed.length > 0 ? t("admin.menu.resetPassword.partial") : t("admin.menu.resetPassword.sent"), failed.length > 0 ? "warning" : "success");
				track("admin_password_reset_success", { user_id: userId, channels: channels.join(","), failed_channels: failed.length, source: "panel" });
			} catch (e) {
				// Never silent: the admin must know whether the link went out.
				if (e instanceof Error) {
					trackError("admin_password_reset_error", { user_id: userId, channels: channels.join(","), source: "panel", error_message: e.message });
					showErrorSnackbar(e.toString(), {}, e);
				}
			} finally {
				setIsResettingPassword(false);
			}
		},
		[isResettingPassword, showErrorSnackbar, showSnackbar, t, track, trackError],
	);
	const [roleFilter, setRoleFilter] = useState<AdminRole | null>(null);
	// Controlled only so the footer count can see the query; the grid still filters.
	const [search, setSearch] = useState("");

	const originalById = useMemo(() => {
		const m = new Map<string, AdminUser>();
		for (const u of originalExistingUsers) {
			if (u.id) m.set(u.id, u);
		}
		return m;
	}, [originalExistingUsers]);

	// newUsers is rendered reversed so the most recently appended sits at the top; the
	// _gridId is bound to the parent-owned key (not the array index) so React keys stay
	// stable across additions/removals and the flash animation runs on the right row.
	const rows = useMemo<AdminUserRow[]>(() => {
		// Without the matrix a customization is indistinguishable from a default, so mark nothing.
		const customizedOf = (user: AdminUser): AdminPermission[] => (roleDefaults ? customizedPermissions(user, roleDefaults) : []);
		return [
			...newUsers
				.map((u, i) => ({
					...u,
					_gridId: newUserKeys[i] ?? `new-fallback-${i}`,
					_isNewRow: true as const,
					_rowIndex: i,
					_isEdited: false,
					_customized: customizedOf(u),
				}))
				.reverse(),
			...existingUsers.map((u, i) => ({
				...u,
				_gridId: `existing-${u.id ?? `idx-${i}`}`,
				_isNewRow: false as const,
				_rowIndex: i,
				_isEdited: isExistingRowEdited(u, u.id ? originalById.get(u.id) : undefined),
				_customized: customizedOf(u),
			})),
		];
	}, [newUsers, newUserKeys, existingUsers, originalById, roleDefaults]);

	// A draft row is always shown: it has no roles yet, so a filter would hide what was just added.
	const visibleRows = useMemo(
		() => (roleFilter === null ? rows : rows.filter((row) => row._isNewRow || row.adminRoles.includes(roleFilter))),
		[rows, roleFilter],
	);

	// What the grid actually shows: it searches on top of the rows we pass it.
	const shownRows = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return visibleRows;
		return visibleRows.filter((row) => SEARCHABLE_COLUMNS.some((column) => extractSearchValue(column, row).toLowerCase().includes(query)));
	}, [visibleRows, search]);

	const roleCounts = useMemo(() => {
		const counts = new Map<AdminRole, number>();
		for (const role of ADMIN_ROLES) counts.set(role, 0);
		for (const row of rows) {
			for (const role of row.adminRoles) counts.set(role, (counts.get(role) ?? 0) + 1);
		}
		return counts;
	}, [rows]);

	const openRow = useMemo(() => rows.find((row) => row._gridId === openPanelGridId) ?? null, [rows, openPanelGridId]);

	const lastHandledNewRowRequest = useRef(0);
	useEffect(() => {
		if (newRowRequestCount === lastHandledNewRowRequest.current) return;
		lastHandledNewRowRequest.current = newRowRequestCount;
		const newest = newUserKeys[newUserKeys.length - 1];
		if (newest) setOpenPanelGridId(newest);
	}, [newRowRequestCount, newUserKeys]);

	const isRowDisabled = useCallback(
		(row: AdminUserRow): boolean => {
			if (isUpdatingUsers) return true;
			if (row.rowDisabled) return true;
			// Don't let a user disable themselves out of the department.
			if (!row._isNewRow && currentUserId !== undefined && row.id === currentUserId) return true;
			return false;
		},
		[isUpdatingUsers, currentUserId],
	);

	/**
	 * Replaces the whole row, for changes touching several fields at once. Picking a role rewrites the
	 * permission checkboxes too, and both must land in one state update or the grid would briefly show
	 * new roles with old checkboxes. Strips the `_`-prefixed grid fields.
	 */
	const replaceRow = useCallback(
		(row: AdminUserRow, next: AdminUser) => {
			const setter = row._isNewRow ? setNewUsers : setExistingUsers;
			setter((prev) => {
				const updated = [...prev];
				updated[row._rowIndex] = next;
				return updated;
			});
		},
		[setNewUsers, setExistingUsers],
	);

	/**
	 * Closes without saving, restoring the row to its last-saved values.
	 *
	 * Every exit uses this — Cancel, X, Escape, scrim — because the save sends every row, so an edit
	 * left behind would be written by the next person's Save. A draft row is dropped instead.
	 */
	const discardPanelEdits = useCallback(() => {
		if (!openRow) {
			setOpenPanelGridId(null);
			return;
		}
		if (openRow._isNewRow) {
			onDiscardNewUser?.(openRow._rowIndex);
			setOpenPanelGridId(null);
			return;
		}
		const baseline = openRow.id ? originalById.get(openRow.id) : undefined;
		if (baseline) replaceRow(openRow, baseline);
		setOpenPanelGridId(null);
	}, [openRow, onDiscardNewUser, originalById, replaceRow]);

	/** Clicking a row opens its panel, unless the click hit the kebab or another control. */
	const handleRowClick = useCallback((params: { row: AdminUserRow }, event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest("input, button, a, [role='button'], [role='combobox'], [role='checkbox'], [role='switch']")) return;
		setOpenPanelGridId(params.row._gridId);
	}, []);

	/** Enter/Space opens the panel too — otherwise a keyboard user's only route is the row menu. */
	const handleCellKeyDown = useCallback((params: { row: AdminUserRow }, event: ReactKeyboardEvent<HTMLElement>) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		const target = event.target as HTMLElement | null;
		if (target?.closest("input, button, a, [role='button'], [role='combobox'], [role='checkbox'], [role='switch']")) return;
		event.preventDefault();
		setOpenPanelGridId(params.row._gridId);
	}, []);

	/** Panel edits use the same row setter as the grid, so both stay in sync. */
	const handlePanelChange = useCallback(
		(next: AdminUser) => {
			if (!openRow) return;
			// Strip the `_`-prefixed grid fields: the panel spreads the whole row, so they would
			// otherwise reach saved state, the save request and analytics.
			const { _gridId, _isNewRow, _rowIndex, _isEdited, _customized, ...user } = next as AdminUserRow;
			replaceRow(openRow, user);
		},
		[openRow, replaceRow],
	);

	/** Summary-bar segments, tagged so a customization or an undeliverable switch stands out. */
	const buildSegments = useCallback(
		(row: AdminUserRow, items: readonly AdminPermissionMeta[]): GrantBarSegment[] =>
			items.map((meta) => {
				const granted = isGranted(row, meta);
				const isCustom = row._customized.includes(meta.permission);
				const isBlocked = granted && needsPhoneNumber(meta.permission) && !row.phoneNumber?.trim();
				const label = t(`admin.permission.${meta.i18nKey}.label`);
				const sourceLine = (): string | undefined => {
					if (roleDefaults) return t(sourceStateKey(permissionSource(row, meta, roleDefaults)));
					// Where the value came from is unknown without the role defaults: no line while they load, and say so if they failed.
					return roleDefaultsFailed ? t("admin.roles.defaults.unavailable") : undefined;
				};
				const state = isBlocked ? t("admin.permission.state.blocked") : sourceLine();
				return {
					id: meta.permission,
					filled: granted,
					tone: isBlocked ? "blocked" : isCustom ? "attention" : "default",
					tooltip: (
						<Box>
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600 }}>{label}</Typography>
							{state && <Typography sx={{ fontSize: FONT_SIZE.CAPTION, opacity: 0.85 }}>{state}</Typography>}
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, mt: 0.5 }}>{t(`admin.permission.${meta.i18nKey}.description`)}</Typography>
						</Box>
					),
				};
			}),
		[t, roleDefaults, roleDefaultsFailed],
	);

	const columns = useMemo<SystemGridColDef<AdminUserRow>[]>(
		() => [
			{
				field: "firstName",
				headerName: t("admin.column.name"),
				minWidth: 170,
				flex: 1,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				excludeFromColumnPicker: true,
				renderCell: ({ row }) => (
					<Typography
						sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}
						aria-label={t("admin.panel.openAria", { name: row.firstName?.trim() || row.username || t("admin.panel.unnamed") })}
					>
						{row.firstName?.trim() || t("admin.panel.unnamed")}
					</Typography>
				),
			},
			{
				field: "_contact",
				headerName: t("admin.column.contact"),
				minWidth: 240,
				flex: 1.6,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				columnPickerGroup: PICKER_GROUP.PLAIN,
				renderCell: ({ row }) => <ContactDetailsCell row={row} />,
			},
			{
				// Holds a list whose change rewrites the permission columns (see `applyRoleChange`).
				field: "adminRoles",
				headerName: t("admin.roles.column"),
				minWidth: 260,
				flex: 1.4,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				columnPickerGroup: PICKER_GROUP.PLAIN,
				renderCell: ({ row }) => (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", minWidth: 0, flexWrap: "wrap" }}>
						{row.adminRoles.length > 0 ? (
							row.adminRoles.map((role) => <MetaChip key={role} label={t(`admin.roles.${role}`)} tone={ROLE_TONES[role]} />)
						) : (
							<MetaChip label={t("admin.roles.empty")} tone="error" dataTestId={`admin-no-role-${row._gridId}`} />
						)}
						{row._customized.length > 0 && (
							<MetaChip
								label={t("admin.custom.badge", { count: row._customized.length })}
								tone="info"
								tooltip={t("admin.custom.tooltip")}
								dataTestId={`admin-custom-${row._gridId}`}
							/>
						)}
					</Box>
				),
			},
			{
				field: "_permissionsSummary",
				headerName: t("admin.column.permissions"),
				headerTooltip: t("admin.column.permissions.tooltip"),
				width: 150,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				columnPickerGroup: PICKER_GROUP.PERMISSIONS,
				renderCell: ({ row }) => (
					<SystemGrantBars
						segments={buildSegments(row, ADMIN_PERMISSION_ITEMS)}
						ariaLabel={t("admin.column.permissions.ariaLabel", { name: row.firstName || row.username })}
						dataTestId={`admin-permissions-bars-${row._gridId}`}
					/>
				),
			},
			{
				field: "_notificationsSummary",
				headerName: t("admin.column.notifications"),
				headerTooltip: t("admin.column.notifications.tooltip"),
				width: 170,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				columnPickerGroup: PICKER_GROUP.NOTIFICATIONS,
				renderCell: ({ row }) => (
					<SystemGrantBars
						segments={buildSegments(row, ADMIN_NOTIFICATION_ITEMS)}
						ariaLabel={t("admin.column.notifications.ariaLabel", { name: row.firstName || row.username })}
						dataTestId={`admin-notifications-bars-${row._gridId}`}
					/>
				),
			},
			// The old wide grid on demand: hidden by default, revealed from the Columns menu. The right
			// view for comparing one permission across everybody.
			...ADMIN_PERMISSION_CATALOG.map((meta) =>
				buildGrantColumn(
					meta,
					t(`admin.permission.${meta.i18nKey}.short`),
					<Box>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600 }}>{t(`admin.permission.${meta.i18nKey}.label`)}</Typography>
						<Typography sx={{ fontSize: FONT_SIZE.CAPTION, mt: 0.5 }}>{t(`admin.permission.${meta.i18nKey}.description`)}</Typography>
					</Box>,
					meta.group,
					(row) => row._customized,
				),
			),
			{
				field: "lastLoginTime",
				headerName: t("admin.column.lastSignIn"),
				headerTooltip: t("admin.column.lastSignIn.tooltip"),
				width: 130,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				columnPickerGroup: PICKER_GROUP.PLAIN,
				renderCell: ({ row }) => <LastSignInCell isoTimestamp={row.lastLoginTime} />,
			},
			{
				field: "_actions",
				// No header text: the kebab is self-explanatory and the word only clipped.
				headerName: "",
				headerTooltip: t("admin.column.actions"),
				width: 60,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				excludeFromColumnPicker: true,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => {
					const isCurrent = !row._isNewRow && currentUserId !== undefined && row.id === currentUserId;
					const rowId = row.id;
					return (
						<MoreActionsMenu
							userId={rowId}
							userName={row.firstName}
							userEmail={row.username}
							allowNotifyEmail={!!row.username}
							allowNotifySMS={!!row.phoneNumber}
							onRemove={!row._isNewRow && rowId && onRemoveUser && !isCurrent ? () => onRemoveUser(rowId) : undefined}
							onOpenPanel={() => setOpenPanelGridId(row._gridId)}
							disabled={isRowDisabled(row)}
						/>
					);
				},
			},
		],
		[isRowDisabled, currentUserId, onRemoveUser, buildSegments, t],
	);

	/** Six columns, no horizontal scroll: every per-permission column starts hidden. */
	const defaultVisibility = useMemo<GridColumnVisibilityModel>(() => Object.fromEntries(ADMIN_PERMISSION_CATALOG.map((meta) => [meta.field, false])), []);

	const columnPicker = useMemo(
		() => ({
			defaultVisibility,
			groupLabels: {
				[PICKER_GROUP.PLAIN]: t("admin.picker.group.plain"),
				[PICKER_GROUP.PERMISSIONS]: t("admin.picker.group.permissions"),
				[PICKER_GROUP.NOTIFICATIONS]: t("admin.picker.group.notifications"),
			},
			// One row per group, so the wide view is one click rather than twelve.
			syntheticColumns: [
				{
					id: "expand-permissions",
					label: t("admin.picker.expandPermissions"),
					controlledFields: ADMIN_PERMISSION_ITEMS.map((meta) => meta.field),
					columnPickerGroup: PICKER_GROUP.PERMISSIONS,
				},
				{
					id: "expand-notifications",
					label: t("admin.picker.expandNotifications"),
					controlledFields: ADMIN_NOTIFICATION_ITEMS.map((meta) => meta.field),
					columnPickerGroup: PICKER_GROUP.NOTIFICATIONS,
				},
			],
		}),
		[defaultVisibility, t],
	);

	const headerGroups = useMemo(
		() => ({
			labels: {
				[PICKER_GROUP.PERMISSIONS]: t("admin.picker.group.permissions"),
				[PICKER_GROUP.NOTIFICATIONS]: t("admin.picker.group.notifications"),
			},
		}),
		[t],
	);

	const roleFilterChips = useMemo(
		() => [
			{ value: "" as AdminRole | "", label: `${t("admin.filter.all")} ${rows.length}` },
			...ADMIN_ROLES.map((role) => ({ value: role as AdminRole | "", label: `${t(`admin.roles.${role}`)} ${roleCounts.get(role) ?? 0}` })),
		],
		[rows.length, roleCounts, t],
	);

	return (
		<Box component="section" sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1, pt: 2 }}>
			<SystemDataGrid<AdminUserRow>
				rows={visibleRows}
				columns={columns}
				getRowId={(r) => r._gridId}
				searchableColumns={SEARCHABLE_COLUMNS}
				searchValue={search}
				onSearchChange={setSearch}
				searchPlaceholder={t("admin.search.placeholder")}
				dataTestId="team-admins"
				localStorageKey={teamAdminsGridKey}
				columnPicker={columnPicker}
				headerGroups={headerGroups}
				leadingToolbarNodes={
					<SystemChips<AdminRole | "">
						items={roleFilterChips}
						selectedItems={roleFilterChips.filter((chip) => chip.value === (roleFilter ?? ""))}
						onChange={(chip) => setRoleFilter(chip.value === "" ? null : (chip.value as AdminRole))}
						variant="outlined"
						forceFitContent
						dataTestId="admin-role-filter"
					/>
				}
				trailingToolbarNodes={actionNodes ? <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>{actionNodes}</Box> : undefined}
				sx={(theme) => ({
					// The row opens the panel, so it reads as clickable.
					"& .MuiDataGrid-row": { cursor: "pointer" },
					"& .MuiDataGrid-row.admin-row-locked": { cursor: "default" },
					// One ellipsized line per header; the full name is in `headerTooltip`. Wrapping makes a
					// narrow column stack two words and clip the rest.
					"& .MuiDataGrid-columnHeaderTitle": {
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					},
					// Keyframe endpoints match the static .admin-new-row background so the
					// finite animation lands without a snap on its last frame.
					"@keyframes admin-new-row-flash": {
						"0%, 100%": { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
						"50%": { backgroundColor: alpha(theme.palette.primary.main, 0.22) },
					},
					"& .MuiDataGrid-row.admin-new-row": {
						backgroundColor: alpha(theme.palette.primary.main, 0.06),
						borderLeft: `3px solid ${theme.palette.primary.main}`,
						animation: "admin-new-row-flash 1.5s ease-in-out 2",
					},
					"& .MuiDataGrid-row.admin-edited-row": {
						backgroundColor: alpha(theme.palette.warning.main, 0.06),
						borderLeft: `3px solid ${theme.palette.warning.main}`,
					},
					"@media (prefers-reduced-motion: reduce)": {
						"& .MuiDataGrid-row.admin-new-row": {
							animation: "none",
						},
					},
				})}
				dataGridProps={{
					rowHeight: 64,
					columnHeaderHeight: 56,
					getRowClassName: ({ row }) =>
						[row._isNewRow ? "admin-new-row" : row._isEdited ? "admin-edited-row" : "", isRowDisabled(row) ? "admin-row-locked" : ""].filter(Boolean).join(" "),
					onRowClick: handleRowClick,
					onCellKeyDown: handleCellKeyDown,
				}}
			/>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 0.5, pb: 1 }}>
				<BarLegend />
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }} data-testid="admin-footer-count">
					{t("admin.footer.count", { shown: shownRows.length, total: rows.length })}
				</Typography>
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, ml: "auto" }}>{t("admin.footer.perPerson")}</Typography>
			</Box>
			<AdminAccessPanel
				row={openRow}
				defaults={roleDefaults}
				open={openRow !== null}
				onClose={discardPanelEdits}
				onChange={handlePanelChange}
				onSave={async () => {
					// Close only once it committed: otherwise a validation failure or the confirm dialog
					// hides behind a panel that looked saved.
					const committed = await onSave?.();
					if (committed !== false) setOpenPanelGridId(null);
				}}
				isSaving={isUpdatingUsers}
				disabled={openRow ? isRowDisabled(openRow) : true}
				isDirty={openRow ? openRow._isNewRow || openRow._isEdited : false}
				isNew={openRow?._isNewRow ?? false}
				onResetPassword={
					openRow?.id && (openRow.username || openRow.phoneNumber)
						? () => resetPasswordForOpenRow(openRow.id as string, !!openRow.username, !!openRow.phoneNumber)
						: undefined
				}
				onSetPassword={userInfo?.support && openRow?.id ? () => setPanelSetPasswordOpen(true) : undefined}
				isResettingPassword={isResettingPassword}
				onDiscardNew={discardPanelEdits}
				onCancelEdits={discardPanelEdits}
			/>
			{openRow?.id && (
				<SetPasswordDialog open={panelSetPasswordOpen} onClose={() => setPanelSetPasswordOpen(false)} userId={openRow.id} userName={openRow.firstName} />
			)}
		</Box>
	);
};
